import {
  Canvas,
  type ThreeEvent,
  useFrame,
  useThree,
} from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import {
  buildTintedModel,
  type DrobbitAnimationMode,
  DESIRED_HEIGHT,
  DROBBIT_GLB,
  findDrobbitActionClips,
} from "../lib/drobbit-model";

export const AVATAR_COLOR_OPTIONS = [
  "#000000",
  "#ff4444",
  "#ff922b",
  "#ffd200",
  "#51cf66",
  "#2ad4ff",
  "#5c7cfa",
  "#cc5de8",
  "#ff6b9d",
  "#ffffff",
];

function PreviewModel({
  color,
  interactive,
  animationMode,
}: {
  color: string;
  interactive: boolean;
  animationMode: DrobbitAnimationMode;
}) {
  const { scene, animations } = useGLTF(DROBBIT_GLB, true) as {
    scene: THREE.Group;
    animations: THREE.AnimationClip[];
  };
  const { camera, gl } = useThree();
  const wrapperRef = useRef<THREE.Group>(null);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const actionsRef = useRef<{
    walk: THREE.AnimationAction | null;
    dance: THREE.AnimationAction | null;
    wave: THREE.AnimationAction | null;
    sit: THREE.AnimationAction | null;
  }>({
    walk: null,
    dance: null,
    wave: null,
    sit: null,
  });
  const modeRef = useRef<DrobbitAnimationMode>(animationMode);
  const yawRef = useRef(-Math.PI * 0.15);
  const dragRef = useRef<{
    active: boolean;
    pointerId: number | null;
    startX: number;
    startYaw: number;
  }>({
    active: false,
    pointerId: null,
    startX: 0,
    startYaw: yawRef.current,
  });

  const tinted = useMemo(() => buildTintedModel(scene, color), [scene, color]);
  const applyMode = (nextMode: DrobbitAnimationMode) => {
    const { walk, dance, wave, sit } = actionsRef.current;
    if (!walk && !dance && !wave && !sit) return;
    const prevMode = modeRef.current;

    if (nextMode === "walk") {
      if (walk && prevMode !== "walk") {
        walk.reset();
      }
      walk?.play();
      walk?.setEffectiveWeight(1);
      walk?.setEffectiveTimeScale(1);
      dance?.setEffectiveWeight(0);
      wave?.setEffectiveWeight(0);
      return;
    }

    if (nextMode === "dance") {
      if (dance && prevMode !== "dance") {
        dance.reset();
      }
      dance?.play();
      dance?.setEffectiveWeight(1);
      dance?.setEffectiveTimeScale(1);
      walk?.setEffectiveWeight(0);
      wave?.setEffectiveWeight(0);
      return;
    }

    if (nextMode === "wave") {
      if (wave && prevMode !== "wave") {
        wave.reset();
      }
      wave?.play();
      wave?.setEffectiveWeight(1);
      wave?.setEffectiveTimeScale(1);
      walk?.setEffectiveWeight(0);
      dance?.setEffectiveWeight(0);
      return;
    }

    if (nextMode === "sit") {
      if (sit && prevMode !== "sit") {
        sit.reset();
      }
      sit?.play();
      sit?.setEffectiveWeight(1);
      sit?.setEffectiveTimeScale(1);
      walk?.setEffectiveWeight(0);
      dance?.setEffectiveWeight(0);
      wave?.setEffectiveWeight(0);
      return;
    }

    walk?.setEffectiveWeight(0);
    dance?.setEffectiveWeight(0);
    wave?.setEffectiveWeight(0);
    sit?.setEffectiveWeight(0);
  };

  useEffect(() => {
    const mixer = new THREE.AnimationMixer(tinted.group);
    mixerRef.current = mixer;
    const clips = findDrobbitActionClips(animations);
    const walk = clips.walk ? mixer.clipAction(clips.walk) : null;
    const dance = clips.dance ? mixer.clipAction(clips.dance) : null;
    const wave = clips.wave ? mixer.clipAction(clips.wave) : null;
    const sit = clips.sit ? mixer.clipAction(clips.sit) : null;
    if (!walk || !dance || !wave) {
      console.warn("[AvatarPreviewCanvas] Missing expected clips", {
        expected: ["Walk", "Dance", "Wave"],
        available: animations.map((clip) => clip.name),
      });
    }
    actionsRef.current = { walk, dance, wave, sit };
    for (const action of [walk, dance, wave, sit]) {
      if (!action) continue;
      action.enabled = true;
      const isWave = action === wave;
      action.setLoop(isWave ? THREE.LoopOnce : THREE.LoopRepeat, Infinity);
      action.clampWhenFinished = isWave;
      action.setEffectiveWeight(0);
      action.setEffectiveTimeScale(1);
      action.play();
    }
    modeRef.current = "idle";
    applyMode(animationMode);
    return () => {
      const {
        walk: currentWalk,
        dance: currentDance,
        wave: currentWave,
        sit: currentSit,
      } = actionsRef.current;
      for (const action of [currentWalk, currentDance, currentWave, currentSit]) {
        action?.stop();
      }
      mixer.stopAllAction();
      mixer.uncacheRoot(tinted.group);
      mixerRef.current = null;
      actionsRef.current = { walk: null, dance: null, wave: null, sit: null };
    };
  }, [animations, tinted]);

  useEffect(() => {
    const previous = modeRef.current;
    if (previous === animationMode) return;
    applyMode(animationMode);
    modeRef.current = animationMode;
  }, [animationMode]);

  useFrame((_, delta) => {
    mixerRef.current?.update(delta);
  });

  useEffect(() => {
    const cam = camera as THREE.OrthographicCamera;
    cam.position.set(2, 1, 2);
    cam.lookAt(0, DESIRED_HEIGHT * 0.5, 0);
    cam.updateProjectionMatrix();
  }, [camera]);

  useEffect(() => {
    if (!interactive) return;
    const dom = gl.domElement;
    const prevCursor = dom.style.cursor;
    dom.style.cursor = "grab";

    const handlePointerMove = (event: PointerEvent) => {
      if (!dragRef.current.active) return;
      const deltaX = event.clientX - dragRef.current.startX;
      const nextYaw = dragRef.current.startYaw + deltaX * 0.01;
      yawRef.current = nextYaw;
      if (wrapperRef.current) {
        wrapperRef.current.rotation.y = nextYaw;
      }
    };

    const stopDrag = (event: PointerEvent) => {
      if (!dragRef.current.active) return;
      if (
        dragRef.current.pointerId != null &&
        event.pointerId !== dragRef.current.pointerId
      ) {
        return;
      }
      dragRef.current.active = false;
      dragRef.current.pointerId = null;
      dom.style.cursor = "grab";
      try {
        dom.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore if capture was never established.
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDrag);
    window.addEventListener("pointercancel", stopDrag);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", stopDrag);
      window.removeEventListener("pointercancel", stopDrag);
      dom.style.cursor = prevCursor;
    };
  }, [gl, interactive]);

  const onPointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!interactive) return;
    event.stopPropagation();
    dragRef.current.active = true;
    dragRef.current.pointerId = event.pointerId;
    dragRef.current.startX = event.clientX;
    dragRef.current.startYaw = yawRef.current;
    gl.domElement.style.cursor = "grabbing";
    try {
      gl.domElement.setPointerCapture(event.pointerId);
    } catch {
      // Fallback still works via global pointer listeners.
    }
  };

  return (
    <group
      ref={wrapperRef}
      position={[0, 0, 0]}
      rotation={[0, yawRef.current, 0]}
      onPointerDown={interactive ? onPointerDown : undefined}
    >
      <mesh
        position={[0, 0.01, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        renderOrder={-1}
      >
        <circleGeometry args={[0.38, 40]} />
        <meshBasicMaterial
          color="#0c1015"
          transparent
          opacity={0.25}
          depthWrite={false}
        />
      </mesh>
      <primitive object={tinted.group} />
    </group>
  );
}

export function AvatarPreviewCanvas({
  color,
  interactive = true,
  animationMode = "idle",
}: {
  color: string;
  interactive?: boolean;
  animationMode?: DrobbitAnimationMode;
}) {
  return (
    <Canvas
      orthographic
      camera={{
        position: [2, 1, 2],
        zoom: 180,
        near: 0.1,
        far: 50,
      }}
      style={{ width: "100%", height: "100%" }}
    >
      <ambientLight intensity={0.7} color="#fff8f0" />
      <hemisphereLight intensity={0.5} color="#b8d8f0" groundColor="#e8d4b8" />
      <directionalLight position={[3, 5, 3]} intensity={1.1} color="#fff4e0" />
      <directionalLight
        position={[-3, 3, -2]}
        intensity={0.3}
        color="#d0e8ff"
      />
      <PreviewModel
        color={color}
        interactive={interactive}
        animationMode={animationMode}
      />
    </Canvas>
  );
}

useGLTF.preload(DROBBIT_GLB, true);
